// Reusable framer-motion wrappers — premium healthcare scroll-reveals.
// Import these instead of repeating <motion.div variants={...}> everywhere.
import { motion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: EASE } },
};

export const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 0.6, ease: EASE } },
};

export const scaleIn = {
  hidden: { opacity: 0, scale: 0.96 },
  visible: { opacity: 1, scale: 1, transition: { duration: 0.55, ease: EASE } },
};

export const stagger = (delay = 0.12) => ({
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: delay, delayChildren: 0.05 } },
});

const viewport = { once: true, margin: "-80px" };

/** Scroll-reveal section. Wrap any section to fade-up on view. */
export const Reveal = ({ as = "div", variants = fadeUp, className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag initial="hidden" whileInView="visible" viewport={viewport}
         variants={variants} className={className} {...rest}>
      {children}
    </Tag>
  );
};

/** Stagger container — direct <RevealItem> children animate in sequence. */
export const RevealGroup = ({ as = "div", delay = 0.12, className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag initial="hidden" whileInView="visible" viewport={viewport}
         variants={stagger(delay)} className={className} {...rest}>
      {children}
    </Tag>
  );
};

/** Item inside RevealGroup — uses fadeUp by default. */
export const RevealItem = ({ as = "div", variants = fadeUp, className = "", children, ...rest }) => {
  const Tag = motion[as] || motion.div;
  return (
    <Tag variants={variants} className={className} {...rest}>
      {children}
    </Tag>
  );
};

/** Subtle parallax shift on scroll (low cost, no rAF loop). */
export const Drift = ({ y = 12, className = "", children, ...rest }) => (
  <motion.div
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={viewport}
    transition={{ duration: 0.8, ease: EASE }}
    className={className}
    {...rest}
  >
    {children}
  </motion.div>
);
